import { X } from "lucide-react";
import { useEffect, useCallback } from "react";

interface SlideOverProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    description?: string;
    children: React.ReactNode;
    footer?: React.ReactNode;
    wide?: boolean;
}

export function SlideOver({ isOpen, onClose, title, description, children, footer, wide }: SlideOverProps) {
    // Escape key handler
    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        },
        [onClose]
    );

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
            document.addEventListener("keydown", handleKeyDown);
        } else {
            document.body.style.overflow = "unset";
        }
        return () => {
            document.body.style.overflow = "unset";
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, handleKeyDown]);

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
                    }`}
                onClick={onClose}
            />

            {/* Panel */}
            <div
                className={`fixed inset-y-0 right-0 z-50 flex max-w-full transition-transform duration-300 ease-out ${isOpen ? "translate-x-0" : "translate-x-full"
                    }`}
            >
                <div className={`w-screen ${wide ? "max-w-2xl" : "max-w-md"}`}>
                    <div className="flex h-full flex-col bg-white shadow-2xl">
                        {/* Header */}
                        <div className="bg-secondary px-6 py-5 flex-shrink-0">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-1 h-6 bg-primary rounded-full" />
                                    <h2 className="text-base font-display font-semibold text-white uppercase tracking-wider">
                                        {title}
                                    </h2>
                                </div>
                                <button
                                    type="button"
                                    className="p-1.5 rounded-md text-white/50 hover:text-white hover:bg-white/10 transition-all focus:outline-none"
                                    onClick={onClose}
                                >
                                    <span className="sr-only">Close panel</span>
                                    <X className="h-5 w-5" aria-hidden="true" />
                                </button>
                            </div>
                            {description && (
                                <p className="text-sm text-white/60 mt-1 ml-4">{description}</p>
                            )}
                        </div>

                        {/* Content */}
                        <div className="relative flex-1 overflow-y-auto px-6 py-6">
                            {children}
                        </div>

                        {/* Footer */}
                        {footer && (
                            <div className="flex flex-shrink-0 justify-end px-6 py-4 border-t border-accent/10 gap-3 bg-surface/50">
                                {footer}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
